import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";

// Ant Design
import { Button, Card, Table, Typography, Pagination } from "antd";
import { PlusOutlined } from "@ant-design/icons";

import Footer from "../../../components/Footer";
import NavBar from "../../../components/NavBar";
import SideBar from "../../../components/SideBar";

const { Title, Text } = Typography;

const TOKENS_SINISTRE = [
  "{CODE_COMPAGNIE}",
  "{POINT_VENTE}",
  "{CATEGORIE}",
  "{ORGANISME}",
  "{ANNEE2}",
  "{ANNEE4}",
  "{SEQ:n}",
  "{PFX}",
  "{TYPE_SIN}",
];

const DEFAULT_SINISTRE = {
  pattern: "{PFX}{CATEGORIE}{ORGANISME}{TYPE_SIN}{SEQ:5}/{ANNEE2}",
  sequenceLength: 5,
  anneeFormat: "ANNEE2",
  prefixe: "S",
  portee: "ANNUELLE",
  actif: true,
};

// ===================== HELPERS =====================
function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
function replaceAllLiteral(str, search, replacement) {
  return str.replace(new RegExp(escapeRegExp(search), "g"), replacement);
}
function validatePattern(pattern, allowedTokens) {
  if (!pattern) return { ok: false, errors: ["Le pattern est vide."] };
  const tokensInPattern = Array.from(
    pattern.matchAll(/\{([A-Z_]+(?::\d+)?)\}/g)
  ).map((m) => `{${m[1]}}`);
  const isSeq = (t) => /^\{SEQ:\d+\}$/.test(t);
  const unknowns = tokensInPattern.filter((t) => {
    if (isSeq(t)) return !allowedTokens.includes("{SEQ:n}");
    return !allowedTokens.includes(t);
  });
  const errors = [];
  if (unknowns.length) errors.push(`Tokens inconnus: ${unknowns.join(", ")}`);
  if ((pattern.match(/\{SEQ:/g) || []).length > 1)
    errors.push("Un seul token {SEQ:n} est autorisé.");
  return { ok: errors.length === 0, errors };
}
function buildNumeroPreview({ cfg, now = new Date(), sample = {} }) {
  const yearFull = String(now.getFullYear());
  const year2 = yearFull.slice(-2);
  const seqLen = (() => {
    const m = (cfg.pattern || "").match(/\{SEQ:(\d+)\}/);
    if (m) return parseInt(m[1], 10);
    return cfg.sequenceLength || 5;
  })();
  const seqPadded = String(sample.SEQ || 0).padStart(seqLen, "0");
  const map = {
    "{COMPAGNIE}": sample.COMPAGNIE || "12200",
    "{POINT_VENTE}": sample.POINT_VENTE || "AG01",
    "{CATEGORIE}": sample.CATEGORIE || "M",
    "{ORGANISME}": sample.ORGANISME || "12",
    "{ANNEE2}": year2,
    "{ANNEE4}": yearFull,
    "{TYPE_SIN}": sample.TYPE_SIN || "M",
    "{PFX}": cfg.prefixe || "S",
  };
  let out = cfg.pattern || "";
  out = out.replace(/\{SEQ:(\d+)\}/g, seqPadded);
  Object.entries(map).forEach(([k, v]) => {
    out = replaceAllLiteral(out, k, v ?? "");
  });
  return out;
}
function resolveApiBase(apiBase) {
  return apiBase ?? process.env.REACT_APP_API_URL ?? "";
}
function buildEndpoint(apiBase) {
  const base = resolveApiBase(apiBase);
  return `${base}${base.endsWith("/") ? "" : base ? "/" : ""}api/numero-config`;
}

// ===================== MODAL =====================
export function NumeroSinistreAddModal({
  show,
  handleClose,
  onAdd,
  existingItems,
  apiBase,
  authToken,
}) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(DEFAULT_SINISTRE);
  const [seqExample, setSeqExample] = useState(12);
  const [typeSinExample, setTypeSinExample] = useState("M");
  const [patternErrors, setPatternErrors] = useState([]);

  useEffect(() => {
    if (show) {
      setForm({ ...DEFAULT_SINISTRE });
      setSeqExample(12);
      setTypeSinExample("M");
      setPatternErrors([]);
    }
  }, [show]);

  const preview = useMemo(
    () =>
      buildNumeroPreview({
        cfg: form,
        sample: { SEQ: seqExample, TYPE_SIN: typeSinExample },
      }),
    [form, seqExample, typeSinExample]
  );

  const onChange = (key, value) => {
    const next = { ...form, [key]: value };
    setForm(next);
    if (key === "pattern") {
      const check = validatePattern(value, TOKENS_SINISTRE);
      setPatternErrors(check.ok ? [] : check.errors);
    }
  };

  const isDuplicate = (pattern) => {
    const normalized = (pattern || "").trim().toLowerCase();
    return Array.isArray(existingItems)
      ? existingItems.some(
          (it) =>
            (it?.type === "SINISTRE" || it?.typeNumero === "SINISTRE") &&
            (it?.pattern || "").trim().toLowerCase() === normalized
        )
      : false;
  };

  const headers = () => ({
    ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    "Content-Type": "application/json",
  });

  const onSubmit = async (e) => {
    e?.preventDefault?.();

    if (!form.pattern?.trim()) {
      toast.warning("Le pattern ne peut pas être vide.");
      return;
    }
    if (patternErrors.length) {
      toast.error("Le pattern est invalide.");
      return;
    }
    if (isDuplicate(form.pattern)) {
      toast.error("Un pattern identique existe déjà.");
      return;
    }

    setLoading(true);
    const endpoint = buildEndpoint(apiBase);
    const payload = { type: "SINISTRE", config: { ...form } };

    try {
      const res = await axios.post(endpoint, payload, { headers: headers() });
      toast.success("Numéro de sinistre ajouté avec succès.");
      onAdd && onAdd(res.data);
      handleClose && handleClose();
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de l'ajout.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Body>
        <button type="button" className="btn-close" onClick={handleClose}></button>

        <div className="text-center mb-4">
          <h4>Ajouter un numéro de sinistre</h4>
          <p>Définissez le pattern et les paramètres de numérotation.</p>
        </div>

        <form className="row g-3" onSubmit={onSubmit}>
          {/* Pattern */}
          <div className="col-12">
            <label className="form-label">Pattern</label>
            <input
              type="text"
              className="form-control"
              value={form.pattern}
              onChange={(e) => onChange("pattern", e.target.value)}
            />
            {patternErrors.length > 0 && (
              <div className="alert alert-warning mt-2">
                {patternErrors.map((e, i) => (
                  <div key={i}>{e}</div>
                ))}
              </div>
            )}
          </div>

          {/* Longueur + Année */}
          <div className="col-md-6">
            <label className="form-label">Longueur séquence</label>
            <input
              type="number"
              min={1}
              className="form-control"
              value={form.sequenceLength}
              onChange={(e) => onChange("sequenceLength", Number(e.target.value))}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">Format année</label>
            <select
              className="form-select"
              value={form.anneeFormat}
              onChange={(e) => onChange("anneeFormat", e.target.value)}
            >
              <option value="ANNEE2">2 chiffres</option>
              <option value="ANNEE4">4 chiffres</option>
            </select>
          </div>

          {/* Préfixe + Portée */}
          <div className="col-md-6">
            <label className="form-label">Préfixe</label>
            <input
              type="text"
              className="form-control"
              value={form.prefixe}
              onChange={(e) => onChange("prefixe", e.target.value)}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">Portée</label>
            <select
              className="form-select"
              value={form.portee}
              onChange={(e) => onChange("portee", e.target.value)}
            >
              <option value="ANNUELLE">ANNUELLE</option>
              <option value="ORGANISME">PAR ORGANISME</option>
              <option value="POINT_VENTE">PAR POINT DE VENTE</option>
              <option value="GLOBALE">GLOBALE</option>
            </select>
          </div>

          {/* Actif */}
          <div className="col-12">
            <div className="form-check form-switch">
              <input
                type="checkbox"
                className="form-check-input"
                checked={!!form.actif}
                onChange={(e) => onChange("actif", e.target.checked)}
              />
              <label className="form-check-label">Actif</label>
            </div>
          </div>

          {/* Exemple + Preview */}
          <div className="col-md-4">
            <label className="form-label">Exemple séquence</label>
            <input
              type="number"
              className="form-control"
              value={seqExample}
              onChange={(e) => setSeqExample(Number(e.target.value))}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">Exemple TYPE_SIN</label>
            <input
              type="text"
              className="form-control"
              value={typeSinExample}
              onChange={(e) =>
                setTypeSinExample((e.target.value || "").toUpperCase())
              }
            />
          </div>
          <div className="col-md-4 d-flex align-items-end">
            <div className="p-2 rounded bg-dark text-white w-100 text-center">
              {preview || "—"}
            </div>
          </div>

          <div className="col-12 text-center mt-3">
            <button type="submit" className="btn btn-primary me-2" disabled={loading}>
              {loading ? "Enregistrement..." : "Enregistrer"}
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleClose}>
              Annuler
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
}

// ===================== PAGE LISTE =====================
export default function NumeroSinistrePage({ apiBase, authToken }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const endpointList = `${buildEndpoint(apiBase)}?type=SINISTRE`;

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios
      .get(endpointList, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      })
      .then((res) => {
        const data = Array.isArray(res.data)
          ? res.data
          : res.data?.items || res.data?.configs || res.data?.data || [];
        if (mounted) setItems(data);
      })
      .catch(() => mounted && setItems([]))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [endpointList, authToken]);

  const columns = [
    {
      title: "PATTERN",
      dataIndex: "pattern",
      key: "pattern",
      render: (v) => <code>{v}</code>,
    },
    { title: "PRÉFIXE", dataIndex: "prefixe", key: "prefixe" },
    { title: "LONGUEUR", dataIndex: "sequenceLength", key: "sequenceLength" },
    { title: "PORTÉE", dataIndex: "portee", key: "portee" },
    {
      title: "ACTIF",
      dataIndex: "actif",
      key: "actif",
      render: (v) => (v ? "Oui" : "Non"),
    },
  ];

  const start = (currentPage - 1) * pageSize;
  const end = start + pageSize;
  const dataPage = items.slice(start, end);

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <SideBar currentMenu="PARAMETRES_SINISTRES" />
        <div className="layout-page">
          <NavBar />
          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">

              {/* 🔹 Titre */}
              <Title level={4} style={{ marginBottom: 16 }}>
                                CONFIGURATION DU NUMERO DE  SINISTRES 
              </Title>

              {/* 🔹 Bouton Ajouter à gauche */}
              <div className="mb-3">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setShowAdd(true)}
                >
                  Configurer un nouveau numéro de Sinistre
                </Button>
              </div>

              {/* 🔹 Table dans une Card */}
              <Card>
                <Table
                  rowKey={(r) => r.id ?? r.pattern}
                  columns={columns}
                  dataSource={dataPage}
                  loading={loading}
                  pagination={false}
                  size="middle"
                  locale={{ emptyText: <Text type="secondary">Aucune configuration</Text> }}
                />
                {items.length > pageSize && (
                  <div className="d-flex justify-content-end me-3 mb-3 mt-3">
                    <Pagination
                      current={currentPage}
                      pageSize={pageSize}
                      total={items.length}
                      onChange={(page, size) => {
                        setCurrentPage(page);
                        setPageSize(size);
                      }}
                      showSizeChanger
                      pageSizeOptions={["5", "10", "20", "50"]}
                      showTotal={(total, range) =>
                        `${range[0]}-${range[1]} sur ${total} éléments`
                      }
                    />
                  </div>
                )}
              </Card>

              {/* 🔹 Modal */}
              <NumeroSinistreAddModal
                show={showAdd}
                handleClose={() => setShowAdd(false)}
                onAdd={(created) => {
                  setItems((arr) => [created, ...arr]);
                  setCurrentPage(1);
                }}
                existingItems={items}
                apiBase={apiBase}
                authToken={authToken}
              />
            </div>
            <Footer />
          </div>
        </div>
      </div>
    </div>
  );
}
